import {
  StatFilter,
  StatFilterType,
} from '../../../compounds/EliasStatsFilter/filter-stats-utils';
import { FinderSelectOptionType } from "../../../compounds/FastFilters/constants";

export const PAGE_TITLE = "NCAA Women's Basketball Team Span Finder";
export const PAGE_SUBTITLE =
  'Search the most complete and accurate NCAA WBB database for team game stats, that occurred within a span of games.';

export const spanRangeOptions: FinderSelectOptionType[] = [
  { id: 'none', label: 'Any' },
  { id: 'single-season', label: 'Single Season' },
  { id: 'current', label: 'Current' },
  { id: 'current-single-season', label: 'Current (single season only)' },
];

export const DEFAULT_SPAN_RANGE_OPTION = spanRangeOptions[0].id;

export const statsFilters: StatFilter[] = [
  {
    id: 'team_win',
    abbr: 'WIN',
    name: 'Team Win',
    type: StatFilterType.equality,
    explanation: '# of Wins',
  },
  {
    id: 'team_loss',
    abbr: 'LOSS',
    name: 'Team Loss',
    type: StatFilterType.equality,
    explanation: '# of Losses',
  },
  {
    id: 'points',
    abbr: 'PTS',
    name: 'Team Points',
    type: StatFilterType.equality,
  },
  {
    id: 'franchise_score',
    abbr: 'TM Game Score',
    name: 'Team Game Score',
    type: StatFilterType.equality,
  },
  {
    id: 'opponent_score',
    abbr: 'OPP Game Score',
    name: 'Opponent Game Score',
    type: StatFilterType.equality,
  },
  {
    id: 'points_difference',
    abbr: 'SC Diff',
    name: 'Score Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'points_per_game',
    abbr: 'PTS/G',
    name: 'Points Per Game',
    type: StatFilterType.equality,
    searchModifier: (value: string, modifierValue: number) => {
      const result = Number(value) * modifierValue;
      return result.toString();
    },
    conflictingStatIds: ['points'],
  },
  {
    id: 'total_rebounds',
    abbr: 'REB',
    name: 'Rebounds',
    type: StatFilterType.equality,
  },
  {
    id: 'total_rebounds_per_game',
    abbr: 'REB/G',
    name: 'Rebounds Per Game',
    type: StatFilterType.equality,
    searchModifier: (value: string, modifierValue: number) => {
      const result = Number(value) * modifierValue;
      return result.toString();
    },
    conflictingStatIds: ['total_rebounds'],
  },
  {
    id: 'assists',
    abbr: 'AST',
    name: 'Assists',
    type: StatFilterType.equality,
  },
  {
    id: 'assists_per_game',
    abbr: 'AST/G',
    name: 'Assists Per Game',
    type: StatFilterType.equality,
    searchModifier: (value: string, modifierValue: number) => {
      const result = Number(value) * modifierValue;
      return result.toString();
    },
    conflictingStatIds: ['assists'],
  },
  {
    id: 'steals',
    abbr: 'STL',
    name: 'Steals',
    type: StatFilterType.equality,
  },
  {
    id: 'blocks',
    abbr: 'BLK',
    name: 'Blocks',
    type: StatFilterType.equality,
  },
  {
    id: 'field_goals_made',
    abbr: 'FG',
    name: 'Field Goals',
    type: StatFilterType.equality,
  },
  {
    id: 'field_goal_attempts',
    abbr: 'FGA',
    name: 'Field Goal Attempts',
    type: StatFilterType.equality,
  },
  {
    id: 'field_goal_percentage',
    abbr: 'FG%',
    name: 'Field Goal %',
    type: StatFilterType.equality,
    explanation: '.xxx format',
  },
  {
    id: 'free_throws_made',
    abbr: 'FT',
    name: 'Free Throws',
    type: StatFilterType.equality,
  },
  {
    id: 'free_throw_attempts',
    abbr: 'FTA',
    name: 'Free Throw Attempts',
    type: StatFilterType.equality,
  },
  {
    id: 'free_throw_percentage',
    abbr: 'FT%',
    name: 'Free Throw %',
    type: StatFilterType.equality,
    explanation: '.xxx format',
  },
  {
    id: 'three_point_field_goals_made',
    abbr: 'FG3M',
    name: 'Three Point Field Goals',
    type: StatFilterType.equality,
  },
  {
    id: 'three_point_field_goal_attempts',
    abbr: 'FG3A',
    name: 'Three Point FG Attempts',
    type: StatFilterType.equality,
  },
  {
    id: 'three_point_field_goal_percentage',
    abbr: 'FG3%',
    name: 'Three Point FG %',
    type: StatFilterType.equality,
    explanation: '.xxx format',
  },
  {
    id: 'turnovers',
    abbr: 'TO',
    name: 'Turnovers',
    type: StatFilterType.equality,
  },
  {
    id: 'personal_fouls',
    abbr: 'PF',
    name: 'Personal Fouls',
    type: StatFilterType.equality,
  },
  {
    id: 'offensive_rebounds',
    abbr: 'OREB',
    name: 'Offensive Rebounds',
    type: StatFilterType.equality,
  },
  {
    id: 'defensive_rebounds',
    abbr: 'DREB',
    name: 'Defensive Rebounds',
    type: StatFilterType.equality,
  },
  {
    id: 'last_game_result',
    abbr: 'WLG',
    name: 'Won Last Game',
    type: StatFilterType.boolean,
    searchModifier: (value: string) => {
      const result = value === '1' ? 'W' : 'L';
      return result;
    },
  },
  {
    id: 'franchise_season_game_number',
    abbr: 'TGN',
    name: 'Team G Num of Seas',
    type: StatFilterType.equality,
  },
  {
    id: 'franchise_win_percentage',
    abbr: 'PCT',
    name: 'Team Win PCT ent. game',
    type: StatFilterType.equality,
  },
  // TODO: JN, 20230814, disabled for the time being
  // {
  //   id: 'seconds',
  //   abbr: 'MIN',
  //   name: 'Minutes',
  //   year: 1955,
  //   type: StatFilterType.equality,
  // },
  {
    id: 'opponent_win_percentage',
    abbr: 'OPCT',
    name: 'Opp Win PCT ent. game',
    type: StatFilterType.equality,
  },
  {
    id: 'win_percentage_difference',
    abbr: 'PDIFF',
    name: 'Win PCT Differential',
    type: StatFilterType.equality,
  },
  {
    id: 'field_goals_difference',
    abbr: 'FGDIFF',
    name: 'Field Goals Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'free_throws_difference',
    abbr: 'FTDIFF',
    name: 'Free Throws Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'three_point_field_goals_difference',
    abbr: 'FG3DIFF',
    name: 'Three Point Field Goals Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'total_rebounds_difference',
    abbr: 'REBDIFF',
    name: 'Rebounds Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'personal_fouls_difference',
    abbr: 'PFDIFF',
    name: 'Personal Fouls Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'turnovers_difference',
    abbr: 'TODIFF',
    name: 'Turnovers Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'steals_difference',
    abbr: 'STLDIFF',
    name: 'Steals Difference',
    type: StatFilterType.equality,
  },
  {
    id: 'field_goal_percentage_difference',
    abbr: 'FG% DIFF',
    name: 'Field Goal Percentage Difference',
    type: StatFilterType.equality,
  },
  
  // {
  //   id: 'points_half_1',
  //   abbr: 'PTS H1',
  //   name: 'First-half points',
  //   type: StatFilterType.equality,
  // },
  // {
  //   id: 'points_half_2',
  //   abbr: 'PTS H2',
  //   name: 'Second-half points',
  //   type: StatFilterType.equality,
  // },
  // {
  //   id: 'opponent_points_half_1',
  //   abbr: 'OPP PTS H1',
  //   name: 'Opponent first-half points',
  //   type: StatFilterType.equality,
  // },
  // {
  //   id: 'opponent_points_half_2',
  //   abbr: 'OPP PTS H2',
  //   name: 'Opponent second-half points',
  //   type: StatFilterType.equality,
  // },
  // {
  //   id: 'points_diff_half_1',
  //   abbr: 'SCDIFF H1',
  //   name: 'First-half Score Difference',
  //   type: StatFilterType.equality,
  // },
  // {
  //   id: 'points_diff_half_2',
  //   abbr: 'SCDIFF H2',
  //   name: 'Second-half Score Difference',
  //   type: StatFilterType.equality,
  // },
];
